import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PaletteToggle } from "@/components/PaletteToggle";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import {
  Menu,
  Search,
  ShieldCheck,
  PanelLeftClose,
  PanelLeftOpen,
  ExternalLink,
  LogOut,
  LayoutDashboard,
  Languages,
} from "lucide-react";

const LS_COLLAPSED = "admin_shell_collapsed";

/**
 * AdminShell — dedicated full-bleed console chrome for /admin.
 *
 * Desktop: fixed icon+label rail on the start edge, sticky slim top bar with
 * breadcrumb + global search, content area that uses the whole viewport.
 * Mobile: compact sticky header with a slide-over nav, a horizontally
 * scrollable tab strip, and a fixed bottom section bar so every group is one
 * tap away.
 */
export function AdminShell({
  sections,
  activeGroup,
  activeLeaf,
  onSelectLeaf,
  onOpenCommand,
  children,
}) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isAr = i18n.language === "ar";
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_COLLAPSED) === "1"; } catch { return false; }
  });
  const [navOpen, setNavOpen] = useState(false);
  const [badges, setBadges] = useState({});

  // Cheap counters so "15 waiting approval" is visible without drilling in.
  useEffect(() => {
    let alive = true;
    const fetchBadges = () =>
      client.get("/admin/badges")
        .then((res) => { if (alive) setBadges(res.data || {}); })
        .catch(() => {});
    fetchBadges();
    const id = setInterval(fetchBadges, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [activeLeaf]);

  const groupBadge = (groupId) => {
    if (groupId === "people") {
      return (badges.pending_users || 0) + (badges.new_waitlist || 0) + (badges.unread_contact || 0) + (badges.open_tickets || 0);
    }
    if (groupId === "deals") {
      return badges.type_requests || 0;
    }
    return 0;
  };

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(LS_COLLAPSED, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const group = useMemo(
    () => sections.find((s) => s.id === activeGroup) || sections[0],
    [sections, activeGroup],
  );
  const leaf = useMemo(
    () => (group?.items || []).find((it) => it.id === activeLeaf) || group?.items?.[0],
    [group, activeLeaf],
  );

  // Body gets a console background while the admin is mounted.
  useEffect(() => {
    document.body.classList.add("admin-console");
    return () => document.body.classList.remove("admin-console");
  }, []);

  const pick = (leafId) => {
    onSelectLeaf(leafId);
    setNavOpen(false);
  };

  const toggleLang = () => i18n.changeLanguage(i18n.language === "ar" ? "en" : "ar");

  const renderRail = ({ compact }) => (
    <nav className="flex flex-col gap-1.5" data-testid="admin-rail-nav">
      {sections.map((s) => {
        const Icon = s.icon;
        const isActive = s.id === group?.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => pick(s.items[0].id)}
            title={compact ? s.label : undefined}
            className={`group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-primary/12 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            } ${compact ? "justify-center px-0" : ""}`}
            data-testid={`admin-nav-group-${s.id}`}
          >
            <span
              className={`absolute inset-y-1.5 start-0 w-[3px] rounded-full transition-opacity ${
                isActive ? "bg-primary opacity-100" : "opacity-0"
              }`}
            />
            <Icon className="h-[18px] w-[18px] shrink-0" />
            {!compact && <span className="truncate">{s.label}</span>}
            {groupBadge(s.id) > 0 ? (
              <span
                className={`font-mono text-[10px] font-semibold rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 ${compact ? "absolute -top-0.5 end-1.5" : "ms-auto"}`}
                data-testid={`admin-nav-badge-${s.id}`}
              >
                {groupBadge(s.id)}
              </span>
            ) : (
              !compact && s.items.length > 1 && (
                <span className="ms-auto font-mono text-[10px] opacity-50">{s.items.length}</span>
              )
            )}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-muted/25 flex" data-testid="admin-shell">
      {/* Desktop rail */}
      <aside
        className={`hidden lg:flex flex-col shrink-0 border-e border-border bg-card/80 backdrop-blur transition-[width] duration-300 ${
          collapsed ? "w-[76px]" : "w-[248px]"
        } sticky top-0 h-screen`}
        data-testid="admin-sidebar"
      >
        <div className={`flex items-center gap-2.5 px-4 h-16 border-b border-border ${collapsed ? "justify-center px-0" : ""}`}>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <ShieldCheck className="h-4.5 w-4.5" />
          </span>
          {!collapsed && (
            <div className="min-w-0">
              <p className="font-display text-sm font-semibold leading-tight truncate">
                {t("adminConsole.brand", { defaultValue: "Console" })}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
                {t("adminConsole.brandSub", { defaultValue: "The Saudi Gate" })}
              </p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3">{renderRail({ compact: collapsed })}</div>

        <div className="p-3 border-t border-border space-y-1.5">
          <Link
            to="/dashboard"
            className={`flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed ? "justify-center px-0" : ""}`}
            data-testid="admin-rail-dashboard"
          >
            <LayoutDashboard className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{t("header.dashboard", { defaultValue: "Dashboard" })}</span>}
          </Link>
          <Link
            to="/"
            className={`flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed ? "justify-center px-0" : ""}`}
            data-testid="admin-rail-site"
          >
            <ExternalLink className="h-4 w-4 shrink-0 rtl-flip" />
            {!collapsed && <span>{t("adminConsole.viewSite", { defaultValue: "View site" })}</span>}
          </Link>
          {collapsed ? (
            <div className="flex justify-center"><PaletteToggle /></div>
          ) : (
            <PaletteToggle variant="row" />
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed ? "justify-center px-0" : ""}`}
            data-testid="admin-sidebar-toggle"
            aria-label="Toggle sidebar"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4 rtl-flip" /> : <PanelLeftClose className="h-4 w-4 rtl-flip" />}
            {!collapsed && <span>{t("adminConsole.collapse", { defaultValue: "Collapse" })}</span>}
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header
          className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70"
          data-testid="admin-topbar"
        >
          <div className="flex items-center gap-2 px-3 sm:px-5 h-14 sm:h-16">
            {/* Mobile nav trigger */}
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden rounded-xl h-10 w-10 shrink-0"
                  aria-label="Open admin menu"
                  data-testid="admin-mobile-nav-trigger"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side={isAr ? "right" : "left"} aria-describedby={undefined} className="w-[280px] max-w-[86vw] p-4 overflow-y-auto" data-testid="admin-mobile-nav-panel">
                <div className="flex items-center gap-2.5 pt-6 pb-4 border-b border-border mb-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <ShieldCheck className="h-4 w-4" />
                  </span>
                  <SheetTitle className="font-display text-sm font-semibold">
                    {t("adminConsole.brand", { defaultValue: "Console" })}
                  </SheetTitle>
                </div>
                {sections.map((s) => (
                  <div key={s.id} className="mb-3">
                    <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                      {s.label}
                    </p>
                    <div className="flex flex-col gap-0.5">
                      {s.items.map((it) => (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => pick(it.id)}
                          className={`text-start rounded-lg px-3 py-2 text-sm transition-colors ${
                            it.id === leaf?.id
                              ? "bg-primary/12 text-primary font-medium"
                              : "text-muted-foreground hover:bg-accent"
                          }`}
                          data-testid={`admin-mobile-nav-${it.id}`}
                        >
                          {it.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="mt-4 pt-3 border-t border-border flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={toggleLang}
                    className="rounded-xl gap-2 text-xs flex-1"
                    data-testid="admin-mobile-lang-toggle"
                  >
                    <Languages className="h-4 w-4" />
                    {isAr ? "English" : "العربية"}
                  </Button>
                  <PaletteToggle />
                </div>
              </SheetContent>
            </Sheet>

            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate hidden sm:block">
                {group?.label}
              </p>
              <h1
                className="font-display text-base md:text-lg font-semibold truncate leading-tight"
                data-testid="admin-current-section"
              >
                {leaf?.label || group?.label}
              </h1>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={onOpenCommand}
              className="hidden md:inline-flex rounded-xl gap-2 h-9 text-sm text-muted-foreground border-border"
              data-testid="admin-command-trigger"
            >
              <Search className="h-4 w-4" />
              <span>{t("adminConsole.search", { defaultValue: "Search anything" })}</span>
              <kbd className="ms-2 inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono">
                ⌘K
              </kbd>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onOpenCommand}
              className="md:hidden rounded-xl h-10 w-10"
              aria-label="Search"
              data-testid="admin-command-trigger-mobile"
            >
              <Search className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleLang}
              className="rounded-xl h-10 px-2.5 hidden sm:inline-flex font-semibold text-xs tracking-wide"
              aria-label="Toggle language"
              data-testid="admin-lang-toggle"
            >
              {isAr ? "ENG" : "AR"}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/12 text-primary text-xs font-semibold uppercase"
                  data-testid="admin-user-menu"
                  aria-label="Account"
                >
                  {(user?.name || "A").trim().charAt(0)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium truncate">{user?.name || t("roles.admin", { defaultValue: "Administrator" })}</p>
                  <p className="text-[11px] text-muted-foreground">{t("roles.admin", { defaultValue: "Administrator" })}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" data-testid="admin-menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 me-2" /> {t("header.dashboard", { defaultValue: "Dashboard" })}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/" data-testid="admin-menu-site">
                    <ExternalLink className="h-4 w-4 me-2 rtl-flip" /> {t("adminConsole.viewSite", { defaultValue: "View site" })}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()} data-testid="admin-menu-signout">
                  <LogOut className="h-4 w-4 me-2" /> {t("common.signOut", { defaultValue: "Sign out" })}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Section tabs — one row, scrollable on small screens */}
          {(group?.items?.length || 0) > 1 && (
            <div className="px-2 sm:px-4 pb-2">
              <div
                className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
                data-testid="admin-section-tabs"
              >
                {group.items.map((it) => {
                  const isActive = it.id === leaf?.id;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => pick(it.id)}
                      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium border transition-all duration-200 ${
                        isActive
                          ? "bg-primary text-primary-foreground border-primary shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
                          : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
                      }`}
                      data-testid={`admin-tab-${it.id}`}
                    >
                      {it.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </header>

        <main
          className="flex-1 min-w-0 px-3 sm:px-5 lg:px-7 py-4 sm:py-6 pb-28 lg:pb-10"
          data-testid="admin-content"
        >
          {children}
        </main>
      </div>

      {/* Mobile bottom section bar */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
        data-testid="admin-bottom-nav"
      >
        <div className="grid grid-cols-6">
          {sections.map((s) => {
            const Icon = s.icon;
            const isActive = s.id === group?.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s.items[0].id)}
                className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}
                data-testid={`admin-bottom-nav-${s.id}`}
              >
                <span className={`relative flex h-7 w-7 items-center justify-center rounded-lg ${isActive ? "bg-primary/12" : ""}`}>
                  <Icon className="h-4 w-4" />
                  {groupBadge(s.id) > 0 && (
                    <span
                      className="absolute -top-0.5 -end-0.5 min-w-[15px] rounded-full bg-primary px-1 text-[9px] font-semibold leading-[15px] text-primary-foreground"
                      data-testid={`admin-nav-badge-${s.id}`}
                    >
                      {groupBadge(s.id)}
                    </span>
                  )}
                </span>
                <span className="truncate max-w-[56px]">{s.shortLabel || s.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default AdminShell;
